import React, { useEffect, useMemo, useState } from 'react';
import Overlay from './PlannerOverlay';
import { getPlannerFillPreview, getPlannerTemplates, getPlannerTemplateDiff } from '../../api';
import { fmtDayShort } from './plannerShared';

// "Fill from scenario" and "Save as template" for the week view (Club Planner, Phase 5). The
// server decides everything (services/planner/templates.js); these dialogs show its preview /
// diff and send the choices.

// "3E: MD+1, MD-4, MD-3, MD-2, MD-1, MD"
const scenarioOption = (code, labels) => (labels && labels.length ? `${code}: ${labels.join(', ')}` : code);

const rowStyle = { display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 };

export function FillFromScenarioDialog({ token, isoYear, isoWeek, busy, onClose, onFill }) {
  const [override, setOverride] = useState('');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [decisions, setDecisions] = useState({});

  useEffect(() => {
    setPreview(null);
    getPlannerFillPreview(token, isoYear, isoWeek, override || null)
      .then(p => { setPreview(p); setError(null); })
      .catch(e => setError(e.message));
  }, [token, isoYear, isoWeek, override]);

  const days = preview?.days || [];
  const needsScenario = days.some(d => d.reason === 'No scenario covers this day');
  const willFill = days.filter(d => d.action === 'fill' || (d.existing_blocks && d.block_count && decisions[d.date] === 'replace'));

  const status = d => {
    if (d.action === 'fill') return `Adds ${d.block_count} block${d.block_count === 1 ? '' : 's'}`;
    if (d.existing_blocks && d.block_count) return null; // keep / replace choice below
    return d.reason;
  };

  return (
    <Overlay title={`Fill week ${isoWeek} from scenario`} onClose={onClose} width={620}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        Each day gets the template blocks of its scenario for that day's key (−4, −3, …, GAME). Off days stay empty, and game-day
        blocks follow the kickoff. Days that already have blocks are only changed if you choose Replace. The result is a draft:
        players see nothing until you publish. The blocks are a copy, so changing the template later won't change this week.
      </div>
      {error && <div role="alert" style={{ color: 'var(--pl-err)', fontSize: 13 }}>{error}</div>}
      {!preview && !error && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>}
      {(needsScenario || override) && preview && (preview.templates.length ? (
        <label className="pl-label">Scenario for days no scenario period covers (only scenarios with a saved schedule)
          <select className="pl-input" value={override} onChange={e => setOverride(e.target.value)}>
            <option value="">— none —</option>
            {preview.templates.map(t => (
              <option key={t.scenario_code} value={t.scenario_code}>{scenarioOption(t.scenario_code, t.day_labels)} (v{t.version})</option>
            ))}
          </select>
        </label>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No scenario has a saved schedule yet. Save a week as a template first.</div>
      ))}
      <div>
        {days.map(d => (
          <div key={d.date} style={rowStyle}>
            <div style={{ width: 92, flexShrink: 0, fontWeight: 600 }}>{fmtDayShort(d.date)}</div>
            <div style={{ width: 54, flexShrink: 0 }}>{d.key && <span className="pl-pill">{d.key}</span>}</div>
            <div style={{ width: 92, flexShrink: 0, color: 'var(--text-secondary)' }}>
              {d.scenario_code ? `${d.scenario_code}${d.template_version ? ` · v${d.template_version}` : ''}` : '—'}
              {d.scenario_label && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{d.scenario_label}</div>}
            </div>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {status(d) && <span style={{ color: d.action === 'fill' ? 'var(--pl-ok)' : 'var(--text-secondary)' }}>{status(d)}</span>}
              {d.existing_blocks > 0 && d.block_count > 0 && (
                <span role="radiogroup" aria-label={`${fmtDayShort(d.date)} already has blocks`} style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Has {d.existing_blocks} block{d.existing_blocks === 1 ? '' : 's'}:</span>
                  <label><input type="radio" checked={decisions[d.date] !== 'replace'} onChange={() => setDecisions(x => ({ ...x, [d.date]: 'keep' }))} /> Keep</label>
                  <label><input type="radio" checked={decisions[d.date] === 'replace'} onChange={() => setDecisions(x => ({ ...x, [d.date]: 'replace' }))} /> Replace with {d.block_count} template blocks</label>
                </span>
              )}
              {d.label_mismatch && (
                <span style={{ fontSize: 12, color: 'var(--pl-warn-border)' }}>
                  The scenario calls this day {d.scenario_label}, the fixtures make it {d.key}. The fixtures are used.
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      <button className="pl-btn pl-btn--primary" style={{ alignSelf: 'flex-start' }} disabled={busy || !willFill.length}
        onClick={() => onFill({ decisions, scenario: override || null })}>
        Fill {willFill.length} day{willFill.length === 1 ? '' : 's'}
      </button>
    </Overlay>
  );
}

export function SaveAsTemplateDialog({ token, week, busy, onClose, onSave }) {
  const [codes, setCodes] = useState([]);
  // Default: the scenario most days of this week belong to.
  const likely = useMemo(() => {
    const count = {};
    week.days.forEach(d => { if (d.scenario?.code) count[d.scenario.code] = (count[d.scenario.code] || 0) + 1; });
    return Object.entries(count).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
  }, [week.days]);
  const [scenario, setScenario] = useState(likely);
  const [points, setPoints] = useState('clear');
  const [note, setNote] = useState('');
  const [diff, setDiff] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getPlannerTemplates(token).then(list => {
      setCodes(list.map(t => ({ code: t.scenario_code, current: t.current_version, labels: t.day_labels })));
      if (!scenario && list[0]) setScenario(list[0].scenario_code);
    }).catch(e => setError(e.message));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!scenario) return;
    setDiff(null);
    getPlannerTemplateDiff(token, week.iso_year, week.iso_week, scenario, points)
      .then(d => { setDiff(d); setError(null); })
      .catch(e => setError(e.message));
  }, [token, week.iso_year, week.iso_week, scenario, points]);

  const first = diff && !diff.current_version;
  const nothing = diff && !first && diff.diff.count === 0;

  return (
    <Overlay title="Save this week as template" onClose={onClose} width={600}>
      <label className="pl-label">Scenario
        <select className="pl-input" value={scenario} onChange={e => setScenario(e.target.value)}>
          {!scenario && <option value="">Pick a scenario</option>}
          {codes.map(c => <option key={c.code} value={c.code}>{scenarioOption(c.code, c.labels)}{c.current ? ` (template v${c.current})` : ' (no template yet)'}</option>)}
        </select>
      </label>
      <div role="radiogroup" aria-label="Specific points" style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>Specific points</span>
        <label><input type="radio" checked={points === 'clear'} onChange={() => setPoints('clear')} /> Clear them (they are usually about this week)</label>
        <label><input type="radio" checked={points === 'keep'} onChange={() => setPoints('keep')} /> Keep them in the template</label>
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        Never saved into a template: staff notes, Group 1 / Group 2, meeting slots and named players.
      </div>
      {error && <div role="alert" style={{ color: 'var(--pl-err)', fontSize: 13 }}>{error}</div>}
      {scenario && !diff && !error && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Comparing…</div>}
      {diff && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
          <div style={{ color: 'var(--text-secondary)' }}>
            {first
              ? `First template for ${scenario}: ${diff.day_keys.join(', ')} from this week.`
              : `Compared with ${scenario} v${diff.baseline_version}${diff.baseline_version !== diff.current_version ? ` (the version this week was filled from; current is v${diff.current_version})` : ''}, for ${diff.day_keys.join(', ')}.`}
            {diff.used_all_days && ' No day of this week belongs to this scenario, so all days are used.'}
          </div>
          {nothing && <div style={{ color: 'var(--text-muted)' }}>No differences from the template — nothing to save.</div>}
          {[...diff.diff.changed, ...diff.diff.added, ...diff.diff.removed].map((c, i) => (
            <div key={i} style={{ padding: '4px 8px', borderRadius: 6, background: 'var(--bg-subtle)' }}>{c.text}</div>
          ))}
          {diff.notes.map((n, i) => <div key={`n${i}`} style={{ fontSize: 12, color: 'var(--text-muted)' }}>{n}</div>)}
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Weeks already planned keep their own copy; nothing changes retroactively.
          </div>
        </div>
      )}
      <label className="pl-label">Note (optional)<input className="pl-input" value={note} maxLength={300} onChange={e => setNote(e.target.value)} placeholder="e.g. training 15 min later on −2" /></label>
      <button className="pl-btn pl-btn--primary" style={{ alignSelf: 'flex-start' }} disabled={busy || !diff || nothing}
        onClick={() => onSave({ scenario, points, note })}>
        Save as {scenario} v{diff ? diff.next_version : '…'}
      </button>
    </Overlay>
  );
}
